import React, {
    FunctionComponent,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    Button,
    Col,
    Row as BSRow,
    FormCheck,
    Dropdown,
    DropdownButton,
} from 'react-bootstrap';
import {
    Row,
    downloadSheets,
    generateSheet,
    getPhysicalGradeByRank,
} from '../../../utils';
import { ApplicationState } from '../../../redux/States';
import { ErrorDispatches } from '../../../redux/Dispatches';
import { ConnectedProps, connect } from 'react-redux';
import { AuthedLayout } from '../../../components';
import { useCheckGrade } from '../../../hook';
import {
    SchoolRank,
    Semester,
    StudentPHIAllStatsic,
    UserInfo,
} from '../../../model';
import apis from '../../../apis';
import { toast } from 'react-toastify';

type ExaminationResultsType = {
    physical: {
        [key: string]: {
            [key: string]: string;
        };
    };
    blood: {
        [key: string]: string;
    };
    xRay: {
        [key: string]: string;
    };
    Lab: {
        [key: string]: string;
    };
};

interface Filter {
    category: (keyof StudentPHIAllStatsic)[];
    criteria: string[];
    value: any[];
    matchAll: boolean;
}

interface ExtractedData {
    grade: string;
    no: string;
    name: string;
    seat: string;
    sex: string;
    results: { [key: string]: any };
}

const ExaminationResults: ExaminationResultsType = {
    physical: {
        bloodPressureWaistline: {
            bpResult: '血壓判讀',
            pulseResult: '脈搏判讀',
        },
        eye: {
            strabismus: '斜視',
            trichiasis: '睫毛倒插',
            nystagmus: '眼球震顫',
            blepharoptosis: '眼瞼下垂',
            colorBlind: '辨色力異常',
            e99: '眼科其他',
        },
        earNoseThroat: {
            hearing: '聽力異常',
            eardrum: '耳膜破損',
            earMisshapen: '耳道畸型',
            clp: '唇顎裂',
            articulationDisorders: '構音異常',
            preauricularFistula: '耳前瘻管',
            cerumen: '耵聹栓塞',
            rhinitis: '慢性鼻炎',
            allergicRhinitis: '過敏性鼻炎',
            tonsillitis: '扁桃腺腫大',
            o99: '耳鼻喉其他',
        },
        headNeck: {
            torticollis: '斜頸',
            mass: '異常腫塊',
            goiter: '甲狀腺腫',
            lymphadenectasis: '淋巴腺腫大',
            mass99: '其他異常腫塊',
            n99: '頭頸其他',
        },
        chest: {
            thorax: '胸廓異常',
            heartMurmur: '心雜音',
            arrhythmia: '心律不整',
            wheeze: '呼吸聲異常',
            cardiopulmonary: '心肺疾病',
            cardiopulmonary99: '其他心肺疾病',
            c99: '胸部其他',
        },
        abdomen: {
            abdomen: '腹部異常腫大',
            hernia: '疝氣',
            a99: '腹部其他',
        },
        spineLimbs: {
            scoliosis: '脊柱側彎',
            polydactyly: '多併指（趾）',
            squatting: '蹲踞困難',
            dysarthrosis: '關節變形',
            dysmelia: '肢體畸形',
            dysmelia99: '其他肢體畸形',
            edema: '水腫',
            l99: '四肢其他',
        },
        urogenital: {
            cryptorchidism: '隱睪',
            scrotalSwelling: '陰囊腫大',
            prepuce: '包皮異常',
            varicocele: '精索靜脈曲張',
            u99: '泌尿生殖其他',
        },
        skin: {
            epidermophytosis: '癬',
            wart: '疣',
            purpura: '紫斑',
            scabies: '疥瘡',
            eczema: '溼疹',
            atopicDermatitis: '異位性皮膚炎',
            d99: '皮膚其他',
        },
        teeth: {
            t01: '未治療齲齒',
            t04: '口腔衛生不良',
            t05: '牙結石',
            t08: '牙齦炎',
            t07: '咬合不正',
            t11: '已治療齲齒',
            t12: '上顎恆牙第一大臼齒齲齒經驗',
            t13: '下顎恆牙第一大臼齒齲齒經驗',
            t15: '恆牙臼齒窩溝封填',
            t09: '口腔黏膜異常',
            t99: '口腔其他',
            t16: '牙周病',
            t17: '乳牙待拔牙',
            t03: '待拔牙',
            t18: '贅生牙',
            t02: '缺牙',
            t19: '阻生牙',
        },
    },
    blood: {
        hbResult: '血色素',
        wbcResult: '白血球',
        rbcResult: '紅血球',
        plResult: '血小板',
        mcvResult: '平均血球容積',
        htResult: '血球容積比',
        clResult: '總膽固醇',
        crResult: '肌酸酐',
        uaResult: '尿酸',
        bunResult: '血尿素氮',
        gotResult: 'SGOT',
        gptResult: 'SGPT',
        hbsaResult: 'B型肝炎表面抗原',
        antiHBResult: 'B型肝炎表面抗體',
        tgResult: '三酸甘油酯',
        hdlResult: '高密度膽固醇',
        ldlResult: '低密度膽固醇',
        acResult: '飯後血糖',
        pcResult: '空腹血糖',
        hBeAgResult: 'B型肝炎e抗原',
    },
    xRay: {
        xRay: '胸部X光檢查',
        phthisis: '肺結核病徵',
        calcify: '肺結核鈣化',
        c03: '胸廓異常',
        water: '肋膜腔積水',
        c02: '脊柱側彎',
        bigHeart: '心臟肥大',
        brochus: '支氣管擴張',
        x99: 'X光其他',
    },
    Lab: {
        urine: '尿液',
        parasite: '寄生蟲',
    },
};

const Physicalsubcatoryname: Record<string, string> = {
    bloodPressureWaistline: '血壓腰圍',
    eye: '眼',
    earNoseThroat: '耳鼻喉',
    headNeck: '頭頸',
    chest: '胸部',
    abdomen: '腹部',
    spineLimbs: '脊柱四肢',
    urogenital: '泌尿生殖',
    skin: '皮膚',
    teeth: '口腔',
};

function filterDataByIndexValue(
    data: StudentPHIAllStatsic[] | undefined,
    filters: {
        category: (keyof StudentPHIAllStatsic)[];
        criteria: string[];
        value: number[];
        matchAll: boolean;
    }
): StudentPHIAllStatsic[] {
    if (!data || filters.criteria.length === 0) return [];

    const isAbnormalMode = filters.value.includes(1);

    return data.filter(item => {
        return filters.category.some(category => {
            const checkEntry = (entry: any) => {
                const validCriteria = filters.criteria.filter(
                    criteria => entry[criteria] !== undefined
                );

                if (validCriteria.length === 0) {
                    return false;
                }

                const results = validCriteria.map(criteria => {
                    const val = Number(entry[criteria]);

                    if (Number.isNaN(val)) {
                        return false;
                    }

                    if (isAbnormalMode) {
                        return val !== 0 && val !== 9;
                    }

                    return filters.value.includes(val);
                });

                return filters.matchAll
                    ? results.every(Boolean)
                    : results.some(Boolean);
            };

            if (category === 'Lab') {
                const urine = item['urine'];
                const parasite = item['parasite'];

                const labArray = ([] as any[])
                    .concat(
                        Array.isArray(urine)
                            ? urine
                            : urine
                                ? [urine]
                                : []
                    )
                    .concat(
                        Array.isArray(parasite)
                            ? parasite
                            : parasite
                                ? [parasite]
                                : []
                    );

                if (labArray.length === 0) return false;

                return labArray.some(checkEntry);
            }

            const section = item[category];

            if (!Array.isArray(section)) return false;

            return section.some(checkEntry);
        });
    });
}

const borderStyle = {
    color: { rgb: '000000' },
    style: 'thin',
} as const;

const cellAlignStyle = {
    horizontal: 'center',
    vertical: 'center',
} as const;

const mapState = (app: ApplicationState) => ({
    ...app.auth,
});

const mapDispatch = ErrorDispatches;

const connector = connect(mapState, mapDispatch);

type Props = ConnectedProps<typeof connector>;

const checkAllQuery: FunctionComponent<Props> = ({
    user,
    catchErrorForModal,
}) => {
    const [examinedresult1, setexaminedresult1] = useState(false);
    const [examinedresult2, setexaminedresult2] = useState(false);
    const [examinedresult3, setexaminedresult3] = useState(false);
    const [examinedresult9, setexaminedresult9] = useState(false);

    const [hasQueried, setHasQueried] = useState(false);
    const [loading, setLoading] = useState(false);

    const {
        currentSemester = '',
        semesters = {},
        currentRank,
    } = user || {};

    const [currentSem] = useState(currentSemester);

    const {
        year = -1,
        sem = -1,
    } = semesters[currentSem];

    const [student, setStudent] =
        useState<StudentPHIAllStatsic[]>();

    const [radioAction, setRadioAction] =
        useState<boolean>(true);

    const [conditionRadioAction, setConditionRadioAction] =
        useState<boolean>(false);

    const healthGradeOptionsMap: Record<number, number[]> = {
        [SchoolRank.Primary]: [1, 4],
        [SchoolRank.Junior]: [7],
        [SchoolRank.Senior]: [10],
        [SchoolRank.Senior4]: [10],
        [SchoolRank.PrimaryJunior]: [1, 4, 7],
        [SchoolRank.JuniorSenior]: [7, 10],
    };

    const healthGradeOptions =
        healthGradeOptionsMap[currentRank] ?? [1];

    const [healthGrade, setHealthGrade] =
        useState<number>(healthGradeOptions[0]);

    const handleRadioChange = (isNowGrade: boolean) => {
        setRadioAction(isNowGrade);
    };

    const conditionhandleRadioChange = (isAnd: boolean) => {
        setConditionRadioAction(isAnd);
    };

    const handleHealthGradeChange = (grade: number) => {
        setHealthGrade(grade);
    };

    const {
        selectedYearGrade,
        element: selectgradeElement,
    } = useCheckGrade({
        year,
        isNowGrade: radioAction,
    });

    const [selectedItems, setSelectedItems] =
        useState<Record<string, any>>({});

    const [userinfo, setUser] =
        useState<UserInfo>();

    const selectedYearGradeMemo =
        useMemo(
            () => selectedYearGrade,
            [
                selectedYearGrade?.grade,
                selectedYearGrade?.year,
            ]
        );

    const filter = useMemo<Filter>(() => {
        const categories =
            Object.keys(selectedItems) as
            (keyof StudentPHIAllStatsic)[];

        const criteria: string[] = [];

        categories.forEach(category => {
            const items = selectedItems[category];

            if (typeof items === 'object') {
                Object.entries(items).forEach(
                    ([subKey, value]) => {
                        if (
                            value !== null &&
                            typeof value === 'object'
                        ) {
                            Object.entries(value).forEach(
                                ([innerKey, isChecked]) => {
                                    if (isChecked) {
                                        criteria.push(innerKey);
                                    }
                                }
                            );
                        } else if (value) {
                            criteria.push(subKey);
                        }
                    }
                );
            }
        });

        const value: number[] = [];

        if (examinedresult1) value.push(1);
        if (examinedresult2) value.push(2);
        if (examinedresult3) value.push(3);
        if (examinedresult9) value.push(9);

        return {
            category: categories,
            criteria,
            value,
            matchAll: conditionRadioAction,
        };
    }, [
        selectedItems,
        examinedresult1,
        examinedresult2,
        examinedresult3,
        examinedresult9,
        conditionRadioAction,
    ]);

    const [filterData, setFilterData] =
        useState<ExtractedData[]>([]);

    const [selectAll, setSelectAll] = useState({
        physical: false,
        blood: false,
        xRay: false,
        Lab: false,
    });

    const fetchCheckAllQuery = () => {
        if (
            !selectedYearGradeMemo?.grade ||
            !selectedYearGradeMemo?.year
        ) {
            return;
        }

        if (
            selectedYearGradeMemo.grade <
            healthGrade
        ) {
            toast.warning(
                `選擇年級不可小於健檢年級（${healthGrade}年級）`
            );
            return;
        }

        setLoading(true);
        setHasQueried(false);
        setStudent(undefined);

        toast
            .promise(
                apis.getCheckAllQuery(
                    selectedYearGradeMemo.year,
                    selectedYearGradeMemo.grade,
                    healthGrade
                ),
                {
                    pending: '資料載入中......',
                    success: '資料載入成功！',
                    error:
                        '資料載入失敗！請查看錯誤資訊。',
                }
            )
            .then(
                (
                    students:
                        StudentPHIAllStatsic[]
                ) => {
                    setStudent(students);

                    const filtered =
                        filterDataByIndexValue(
                            students,
                            filter
                        );

                    const extracted =
                        extractChineseNames(
                            filtered,
                            ExaminationResults,
                            filter.value,
                            filter.criteria
                        );

                    setFilterData(extracted);
                    setHasQueried(true);
                }
            )
            .catch(catchErrorForModal)
            .finally(() => {
                setLoading(false);
            });

        apis
            .getUserInfo()
            .then((u) => setUser(u))
            .catch(catchErrorForModal);
    };

    useEffect(() => {
        setHasQueried(false);
        setStudent(undefined);
        setFilterData([]);
    }, [
        selectedYearGradeMemo?.grade,
        selectedYearGradeMemo?.year,
        selectedItems,
        examinedresult1,
        examinedresult2,
        examinedresult3,
        examinedresult9,
        conditionRadioAction,
    ]);

    const handleCheckboxChange = (
        category: keyof ExaminationResultsType,
        subCategory: string,
        item: string
    ) => {
        setSelectedItems(prevState => ({
            ...prevState,

            [category]: {
                ...prevState[category],

                ...(subCategory
                    ? {
                        [subCategory]: {
                            ...prevState[category]?.[
                            subCategory
                            ],
                            [item]:
                                !prevState[category]?.[
                                subCategory
                                ]?.[item],
                        },
                    }
                    : {
                        [item]:
                            !prevState[category]?.[
                            item
                            ],
                    }),
            },
        }));
    };

    const handleSelectAllChange = (
        category: keyof ExaminationResultsType,
        isNested: boolean = false,
        subCategory?: string
    ) => {
        const isSelected =
            !selectAll[category];

        setSelectAll(prevState => ({
            ...prevState,
            [category]: isSelected,
        }));

        if (
            isNested &&
            subCategory
        ) {
            const updatedNested:
                Record<string, any> = {};

            Object.keys(
                ExaminationResults[category][
                subCategory
                ]
            ).forEach(item => {
                updatedNested[item] =
                    isSelected;
            });

            setSelectedItems(
                prevState => ({
                    ...prevState,

                    [category]: {
                        ...prevState[category],

                        [subCategory]:
                            updatedNested,
                    },
                })
            );
        } else if (
            category === 'physical'
        ) {
            const updatedCategory:
                Record<string, any> = {};

            Object.keys(
                ExaminationResults[category]
            ).forEach(subCategory => {
                updatedCategory[
                    subCategory
                ] = {};

                Object.keys(
                    ExaminationResults[
                    category
                    ][subCategory]
                ).forEach(item => {
                    updatedCategory[
                        subCategory
                    ][item] = isSelected;
                });
            });

            setSelectedItems(
                prevState => ({
                    ...prevState,
                    [category]:
                        updatedCategory,
                })
            );
        } else {
            const updatedCategory:
                Record<string, boolean> = {};

            Object.keys(
                ExaminationResults[category]
            ).forEach(item => {
                updatedCategory[item] =
                    isSelected;
            });

            setSelectedItems(
                prevState => ({
                    ...prevState,
                    [category]:
                        updatedCategory,
                })
            );
        }
    };

    const createCheckBoxList = (
        category: keyof ExaminationResultsType,
        categoryName: string,
        subCategory?: string
    ) => {
        if (
            subCategory &&
            category === 'physical'
        ) {
            const subCategoryItems =
                Object.entries(
                    ExaminationResults[
                    category
                    ][subCategory]
                );

            return (
                <fieldset
                    key={subCategory}
                    style={{
                        border:
                            '1px solid #000',
                        margin: '5px 0',
                        padding: '10px',
                    }}
                >
                    <legend
                        className="legendbackgropminor"
                        style={{
                            fontSize: '16px',
                            marginBottom: '10px',
                            width: 'fit-content',
                        }}
                    >
                        <FormCheck
                            className="mr-2"
                            label={
                                Physicalsubcatoryname[
                                subCategory
                                ]
                            }
                            checked={
                                selectAll[
                                category
                                ] &&
                                selectedItems[
                                category
                                ]?.[
                                subCategory
                                ] &&
                                Object.keys(
                                    selectedItems[
                                    category
                                    ][
                                    subCategory
                                    ]
                                ).every(
                                    item =>
                                        selectedItems[
                                        category
                                        ]?.[
                                        subCategory
                                        ]?.[
                                        item
                                        ]
                                )
                            }
                            onChange={() =>
                                handleSelectAllChange(
                                    category,
                                    true,
                                    subCategory
                                )
                            }
                        />
                    </legend>

                    <BSRow
                        style={{
                            margin:
                                '0px 10px',
                        }}
                    >
                        {subCategoryItems.map(
                            ([key, label]) => (
                                <Col
                                    xs={2}
                                    key={`${category}_${subCategory}_${key}`}
                                >
                                    <FormCheck
                                        id={`${category}_${subCategory}_${key}`}
                                        className="mr-2"
                                        label={label}
                                        checked={
                                            selectedItems[
                                            category
                                            ]?.[
                                            subCategory
                                            ]?.[
                                            key
                                            ] ||
                                            false
                                        }
                                        onChange={() =>
                                            handleCheckboxChange(
                                                category,
                                                subCategory,
                                                key
                                            )
                                        }
                                    />
                                </Col>
                            )
                        )}
                    </BSRow>
                </fieldset>
            );
        }

        const categoryItems =
            Object.entries(
                ExaminationResults[
                category
                ]
            );

        return (
            <BSRow
                key={category}
                style={{
                    margin: '0px 10px',
                }}
            >
                {categoryItems.map(
                    ([key, label]) => (
                        <Col
                            xs={2}
                            key={`${category}_${key}`}
                        >
                            <FormCheck
                                id={`${category}_${key}`}
                                className="mr-2"
                                label={
                                    label as string
                                }
                                checked={
                                    selectedItems[
                                    category
                                    ]?.[
                                    key
                                    ] ||
                                    false
                                }
                                onChange={() =>
                                    handleCheckboxChange(
                                        category,
                                        '',
                                        key
                                    )
                                }
                            />
                        </Col>
                    )
                )}
            </BSRow>
        );
    };

    const createCategorySections = (
        category:
            keyof ExaminationResultsType,
        categoryName: string
    ) => {
        return (
            <fieldset
                style={{
                    border:
                        '1px solid #000',
                    margin: '5px 0',
                    padding: '10px',
                }}
                key={category}
            >
                <legend
                    className="legendbackgropminor"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        width: 'fit-content',
                    }}
                >
                    <FormCheck
                        id={`chb_${category}`}
                        className="check"
                        checked={
                            selectAll[
                            category
                            ]
                        }
                        onChange={() =>
                            handleSelectAllChange(
                                category
                            )
                        }
                    />

                    <span
                        className="spanbackgropminor"
                        style={{
                            fontSize: '22px',
                            marginLeft: '10px',
                        }}
                    >
                        {categoryName}
                    </span>
                </legend>

                {category ===
                    'physical'
                    ? Object.keys(
                        ExaminationResults[
                        category
                        ]
                    ).map(
                        subCategory =>
                            createCheckBoxList(
                                category,
                                '',
                                subCategory
                            )
                    )
                    : createCheckBoxList(
                        category,
                        categoryName
                    )}
            </fieldset>
        );
    };

    const contentRows: Row[] =
        Object.values(filterData).flatMap(
            (gs) => {
                return checktoRow(
                    gs.grade,
                    gs.no,
                    gs.name,
                    gs.seat,
                    gs.sex,
                    gs.results.join(',')
                );
            }
        );

    const content: Row[] = [
        {
            cells: [
                {
                    value:
                        `${selectedYearGrade?.grade}年級健康檢查詢清單`,
                    merge: {
                        column: 6,
                    },
                },
            ],
        },
        {
            cells: [
                {
                    value: '年',
                    merge: {
                        column: 1,
                    },
                },
                {
                    value: '班',
                    merge: {
                        column: 1,
                    },
                },
                {
                    value: '姓名',
                    merge: {
                        column: 1,
                    },
                },
                {
                    value: '座號',
                    merge: {
                        column: 1,
                    },
                },
                {
                    value: '性別',
                    merge: {
                        column: 1,
                    },
                },
                {
                    value: '健檢結果',
                    merge: {
                        column: 1,
                    },
                },
            ],
        },
        ...contentRows,
    ];

    const title =
        `${selectedYearGrade?.grade}年級健康檢查詢清單`;

    // =========================
    // Excel
    // =========================
    const handleExcelDownload = () => {
        const workSheet =
            generateSheet(
                [
                    ...content,
                    {
                        cells: [
                            {
                                value:
                                    '承辦人:　　　　　　　　　　　　　組長:　　　　　　　　　　　　　主任:　　　　　　　　　　　　　　校長:　　　　　　　　　　　　',
                                style: {
                                    alignment: {
                                        horizontal:
                                            'left',
                                    },
                                },
                            },
                        ],
                    },
                ],
                {
                    alignment:
                        cellAlignStyle,
                }
            );

        downloadSheets(
            title,
            userinfo?.exportFileType,
            {
                sheet: workSheet,
                name: 'st',
            }
        );
    };

    // =========================
    // 列印
    // =========================
    const handlePrint = () => {
        const printWindow =
            window.open(
                '',
                '_blank',
                'width=1200,height=900'
            );

        if (!printWindow) {
            alert(
                '瀏覽器阻擋了列印視窗，請允許彈出式視窗。'
            );
            return;
        }

        const escapeHtml = (
            value: unknown
        ): string => {
            return String(value ?? '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(
                    /"/g,
                    '&quot;'
                )
                .replace(
                    /'/g,
                    '&#039;'
                );
        };

        const tableRows =
            content
                .map(row => {
                    const cells =
                        row.cells
                            .map(cell => {
                                if (
                                    cell.value ===
                                    undefined
                                ) {
                                    return '';
                                }

                                const colSpan =
                                    cell.merge
                                        ?.column
                                        ? ` colspan="${cell.merge.column}"`
                                        : '';

                                const rowSpan =
                                    cell.merge?.row
                                        ? ` rowspan="${cell.merge.row}"`
                                        : '';

                                return `
                                    <td${colSpan}${rowSpan}>
                                        ${escapeHtml(
                                    cell.value
                                )}
                                    </td>
                                `;
                            })
                            .join('');

                    return `
                        <tr>
                            ${cells}
                        </tr>
                    `;
                })
                .join('');

        printWindow.document.open();

        printWindow.document.write(`
            <!DOCTYPE html>
            <html lang="zh-Hant">
            <head>
                <meta charset="UTF-8" />

                <title>
                    ${escapeHtml(title)}
                </title>

                <style>
                    * {
                        box-sizing: border-box;
                    }

                    html,
                    body {
                        margin: 0;
                        padding: 0;

                        font-family:
                            "Microsoft JhengHei",
                            "Noto Sans TC",
                            Arial,
                            sans-serif;

                        color: #000;
                    }

                    .print-page {
                        min-height: 277mm;

                        display: flex;
                        flex-direction: column;
                    }

                    table {
                        width: 100%;

                        border-collapse: collapse;
                        table-layout: fixed;
                    }

                    td {
                        border: 1px solid #000;

                        padding: 6px 4px;

                        text-align: center;
                        vertical-align: middle;

                        font-size: 11px;

                        word-break: break-word;
                    }

                    tr:first-child td {
                        font-size: 18px;
                        font-weight: bold;

                        padding: 10px;
                    }

                    tr:nth-child(2) td {
                        font-weight: bold;
                    }

                    .footer {
    margin-top: auto;
    padding: 18px 8mm 0;
    display: flex;
    font-size: 10px;
}

.footer span {
    width: 25%;
    text-align: left;
}

                    @page {
                        size: A4 landscape;
                        margin: 10mm;
                    }

                    @media print {
                        body {
                            padding: 0;
                        }

                        tr {
                            page-break-inside:
                                avoid;
                            break-inside:
                                avoid;
                        }
                    }
                </style>
            </head>

            <body>
                <div class="print-page">

                    <table>
                        <tbody>
                            ${tableRows}
                        </tbody>
                    </table>

                    <div class="footer">
                        <span>承辦人:</span>
                        <span>組長:</span>
                        <span>主任:</span>
                        <span>校長:</span>
                    </div>

                </div>

                <script>
                    window.onload = function () {
                        window.focus();

                        setTimeout(
                            function () {
                                window.print();
                            },
                            300
                        );
                    };

                    window.onafterprint =
                        function () {
                            window.close();
                        };
                </script>
            </body>
            </html>
        `);

        printWindow.document.close();
    };

    return (
        <AuthedLayout>
            <BSRow className="mb-2 justify-content-start">
                <Col
                    className="d-flex mr-2"
                    xs={2}
                >
                    <FormCheck
                        className="mr-4"
                        value="在學"
                        type="radio"
                        label="在學"
                        name="list-action"
                        checked={
                            radioAction ===
                            true
                        }
                        onChange={() =>
                            handleRadioChange(
                                true
                            )
                        }
                    />

                    <FormCheck
                        className="mr-4"
                        value="歷屆年級"
                        type="radio"
                        label="歷屆年級"
                        name="list-action"
                        checked={
                            radioAction ===
                            false
                        }
                        onChange={() =>
                            handleRadioChange(
                                false
                            )
                        }
                    />
                </Col>

                <Col
                    className="d-flex mr-1"
                    xs={2}
                >
                    {selectgradeElement}
                </Col>

                <Col
                    className="mr-1"
                    xs={1}
                >
                    <div className="mb-1 font-weight-bold">
                        健檢年級：
                    </div>

                    <div>
                        {healthGradeOptions.map(
                            grade => (
                                <div
                                    key={
                                        grade
                                    }
                                >
                                    <FormCheck
                                        className="mr-1"
                                        value={
                                            grade
                                        }
                                        type="radio"
                                        label={`${grade}年級`}
                                        name="health-grade"
                                        checked={
                                            healthGrade ===
                                            grade
                                        }
                                        onChange={() =>
                                            handleHealthGradeChange(
                                                grade
                                            )
                                        }
                                    />
                                </div>
                            )
                        )}
                    </div>
                </Col>

                <Col
                    className="mr-2"
                    xs={2}
                >
                    <div className="mb-1 font-weight-bold">
                        條件符合規則：
                    </div>

                    <div>
                        <FormCheck
                            value="and"
                            type="radio"
                            label="AND（勾選項目皆須符合）"
                            name="condition"
                            checked={
                                conditionRadioAction ===
                                true
                            }
                            onChange={() =>
                                conditionhandleRadioChange(
                                    true
                                )
                            }
                        />
                    </div>

                    <div>
                        <FormCheck
                            value="or"
                            type="radio"
                            label="OR（勾選項目至少一項以上符合）"
                            name="condition"
                            checked={
                                conditionRadioAction ===
                                false
                            }
                            onChange={() =>
                                conditionhandleRadioChange(
                                    false
                                )
                            }
                        />
                    </div>
                </Col>

                <Col
                    className="mr-2"
                    xs={2}
                >
                    <FormCheck
                        label="初檢異常"
                        checked={
                            examinedresult1
                        }
                        onChange={() =>
                            setexaminedresult1(
                                !examinedresult1
                            )
                        }
                        value="1"
                    />

                    <FormCheck
                        label="複檢正常－偽陽性"
                        checked={
                            examinedresult2
                        }
                        onChange={() =>
                            setexaminedresult2(
                                !examinedresult2
                            )
                        }
                        value="2"
                    />

                    <FormCheck
                        label="複檢異常－確診"
                        checked={
                            examinedresult3
                        }
                        onChange={() =>
                            setexaminedresult3(
                                !examinedresult3
                            )
                        }
                        value="3"
                    />

                    <FormCheck
                        label="未受檢"
                        checked={
                            examinedresult9
                        }
                        onChange={() =>
                            setexaminedresult9(
                                !examinedresult9
                            )
                        }
                        value="9"
                    />
                </Col>

                <Col className="mr-1" xs={2}>
                    <div className="d-flex align-items-center">
                        <Button
                            variant="primary"
                            className="text-dark mr-2"
                            disabled={loading}
                            onClick={fetchCheckAllQuery}
                        >
                            查詢
                        </Button>

                        <DropdownButton
                            id="check-all-query-export"
                            title="匯出"
                            variant="success"
                            disabled={!hasQueried || loading}
                        >
                            <Dropdown.Item onClick={handleExcelDownload}>
                                Excel 下載
                            </Dropdown.Item>

                            <Dropdown.Item onClick={handlePrint}>
                                列印
                            </Dropdown.Item>
                        </DropdownButton>
                    </div>
                </Col>
            </BSRow>

            <hr />

            <BSRow>
                <div>
                    {[
                        {
                            category:
                                'physical',
                            name:
                                '身體診察',
                        },
                        {
                            category:
                                'blood',
                            name:
                                '血液檢查',
                        },
                        {
                            category:
                                'xRay',
                            name:
                                'Ｘ光檢查',
                        },
                        {
                            category:
                                'Lab',
                            name:
                                '實驗室檢查',
                        },
                    ].map(item => (
                        <React.Fragment
                            key={
                                item.category
                            }
                        >
                            {createCategorySections(
                                item.category as keyof ExaminationResultsType,
                                item.name
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </BSRow>
        </AuthedLayout>
    );
};

function extractChineseNames(
    data: any[],
    examResults: ExaminationResultsType,
    selectedValues: number[],
    selectedCriteria: string[]
): ExtractedData[] {
    const extractedData:
        ExtractedData[] = [];

    const isAbnormalMode =
        selectedValues.includes(1);

    data.forEach((entry) => {
        const {
            grade,
            no,
            name,
            seat,
            sex,
            classes,
        } = entry;

        const labelValuePairs:
            [string, number][] = [];

        for (
            const categoryKey in examResults
        ) {
            if (
                !Object.prototype.hasOwnProperty.call(
                    examResults,
                    categoryKey
                )
            ) {
                continue;
            }

            const category =
                examResults[
                categoryKey as keyof ExaminationResultsType
                ];

            let entryCategoryData =
                entry[categoryKey];

            if (categoryKey === 'Lab') {
                const selectedLabCriteria =
                    selectedCriteria.filter(
                        c =>
                            c === 'urine' ||
                            c === 'parasite'
                    );

                let rawLabArray:
                    any[] = [];

                if (
                    selectedLabCriteria.includes(
                        'urine'
                    )
                ) {
                    const urine =
                        entry['urine'];

                    rawLabArray =
                        rawLabArray.concat(
                            (
                                Array.isArray(
                                    urine
                                )
                                    ? urine
                                    : urine
                                        ? [
                                            urine,
                                        ]
                                        : []
                            ).map(
                                (
                                    x: any
                                ) => ({
                                    ...x,
                                    __labType:
                                        'urine',
                                })
                            )
                        );
                }

                if (
                    selectedLabCriteria.includes(
                        'parasite'
                    )
                ) {
                    const parasite =
                        entry[
                        'parasite'
                        ];

                    rawLabArray =
                        rawLabArray.concat(
                            (
                                Array.isArray(
                                    parasite
                                )
                                    ? parasite
                                    : parasite
                                        ? [
                                            parasite,
                                        ]
                                        : []
                            ).map(
                                (
                                    x: any
                                ) => ({
                                    ...x,
                                    __labType:
                                        'parasite',
                                })
                            )
                        );
                }

                entryCategoryData =
                    rawLabArray.filter(
                        lab => {
                            const inspect =
                                Number(
                                    lab.inspect ??
                                    1
                                );

                            if (
                                selectedValues.includes(
                                    1
                                ) ||
                                selectedValues.includes(
                                    9
                                )
                            ) {
                                if (
                                    inspect !==
                                    1
                                ) {
                                    return false;
                                }
                            }

                            if (
                                selectedValues.includes(
                                    2
                                ) ||
                                selectedValues.includes(
                                    3
                                )
                            ) {
                                if (
                                    inspect !==
                                    2
                                ) {
                                    return false;
                                }
                            }

                            const key =
                                lab.__labType;

                            const val =
                                Number(
                                    lab[key]
                                );

                            if (
                                Number.isNaN(
                                    val
                                )
                            ) {
                                return false;
                            }

                            return selectedValues.includes(
                                val
                            );
                        }
                    );
            }

            if (!entryCategoryData) {
                continue;
            }

            const processEntry = (
                subEntry: any
            ) => {
                for (
                    const subKey in subEntry
                ) {
                    if (
                        categoryKey ===
                        'physical' &&
                        (
                            subKey ===
                            'cryptorchidism' ||
                            subKey ===
                            'scrotalSwelling' ||
                            subKey ===
                            'prepuce' ||
                            subKey ===
                            'varicocele' ||
                            subKey ===
                            'u99'
                        ) &&
                        sex === '2'
                    ) {
                        continue;
                    }

                    if (
                        !Object.prototype.hasOwnProperty.call(
                            subEntry,
                            subKey
                        )
                    ) {
                        continue;
                    }

                    const subValue =
                        subEntry[subKey];

                    const matched =
                        subValue !==
                        undefined &&
                        subValue !== null &&
                        selectedValues.includes(
                            Number(
                                subValue
                            )
                        );

                    if (
                        categoryKey ===
                        'Lab' &&
                        subKey === 'urine'
                    ) {
                        if (
                            matched &&
                            (category as any)[
                            'urine'
                            ]
                        ) {
                            labelValuePairs.push(
                                [
                                    (
                                        category as any
                                    )[
                                    'urine'
                                    ],
                                    subValue,
                                ]
                            );
                        }

                        continue;
                    }

                    if (
                        categoryKey ===
                        'Lab' &&
                        subKey ===
                        'parasite'
                    ) {
                        if (
                            matched &&
                            (category as any)[
                            'parasite'
                            ]
                        ) {
                            labelValuePairs.push(
                                [
                                    (
                                        category as any
                                    )[
                                    'parasite'
                                    ],
                                    subValue,
                                ]
                            );
                        }

                        continue;
                    }

                    if (
                        subValue !==
                        undefined &&
                        subValue !== null &&
                        subValue !== 0
                    ) {
                        for (
                            const subCatKey in category
                        ) {
                            const subCat =
                                (
                                    category as
                                    Record<
                                        string,
                                        any
                                    >
                                )[
                                subCatKey
                                ];

                            if (
                                typeof subCat ===
                                'object' &&
                                subCat[
                                subKey
                                ]
                            ) {
                                labelValuePairs.push(
                                    [
                                        subCat[
                                        subKey
                                        ],
                                        subValue,
                                    ]
                                );
                            }
                        }

                        if (
                            typeof (
                                category as any
                            )[
                            subKey
                            ] ===
                            'string'
                        ) {
                            labelValuePairs.push(
                                [
                                    (
                                        category as any
                                    )[
                                    subKey
                                    ],
                                    subValue,
                                ]
                            );
                        }
                    }
                }
            };

            if (
                Array.isArray(
                    entryCategoryData
                )
            ) {
                entryCategoryData.forEach(
                    processEntry
                );
            } else {
                processEntry(
                    entryCategoryData
                );
            }
        }

        const latestNoRaw =
            classes[
                classes.length - 1
            ]?.no ?? no;

        const latestNo =
            Number(latestNoRaw) > 100
                ? Number(
                    latestNoRaw
                ) - 100
                : latestNoRaw;

        const finalResults =
            labelValuePairs.length === 0
                ? ['無異狀']
                : labelValuePairs.map(
                    ([label, val]) =>
                        `${label}${val}`
                );

        extractedData.push({
            grade,
            no: latestNo,
            name,
            seat,
            sex,
            results: finalResults,
        });
    });

    return extractedData;
}

function checktoRow(
    grade: string,
    classno: string,
    name: string,
    seat: string,
    sex: string,
    checkdata: string
): Row[] {
    return [
        {
            cells: [
                {
                    value: grade,
                    style: {
                        border: {
                            left:
                                borderStyle,
                        },
                    },
                },
                {
                    value: classno,
                    style: {
                        border: {
                            left:
                                borderStyle,
                        },
                    },
                },
                {
                    value: name,
                    style: {
                        border: {
                            left:
                                borderStyle,
                        },
                    },
                },
                {
                    value: seat,
                    style: {
                        border: {
                            left:
                                borderStyle,
                        },
                    },
                },
                {
                    value:
                        sex === '1'
                            ? '男'
                            : '女',
                    style: {
                        border: {
                            left:
                                borderStyle,
                        },
                    },
                },
                {
                    value: checkdata,
                    style: {
                        border: {
                            left:
                                borderStyle,
                        },
                    },
                },
            ],
        },
    ];
}

export const CheckAllQuery = connector(checkAllQuery);